/**
 * Seeker Search Contracts — Seeker Microservice Wire Types.
 *
 * Wire-format types for the Seeker search microservice (/api/search proxy).
 * Covers search, suggest, progressive SSE, and feedback.
 *
 * CODEGEN:
 * DTOs are derived from auto-generated Zod schemas (zod.gen.ts) produced by
 * @hey-api/openapi-ts from the Seeker OpenAPI spec (seeker/openapi.json).
 * This guarantees wire-format alignment without manual maintenance.
 *
 * Enums using `as const` patterns are kept hand-written (UI-only discriminators).
 * Request interfaces are kept hand-written for frontend mutation composition.
 *
 * snake_case keys: Seeker uses snake_case on the wire (no CamelModel).
 */

import { z } from 'zod';

import {
  zSearchResponse as GeneratedSearchResponse,
  zSearchResult as GeneratedSearchResult,
  zClaimResult as GeneratedClaimResult,
  zFacetCounts as GeneratedFacetCounts,
  zSearchTab as GeneratedSearchTab,
  zSuggestResponse as GeneratedSuggestResponse,
  zFeedbackRequest as GeneratedFeedbackRequest,
} from '@/api/generated/seeker/zod.gen';

// ═══════════════════════════════════════════════════════════════════════
// ENUMS — derived from generated Zod schemas
// ═══════════════════════════════════════════════════════════════════════

export const SeekerTabEnum = {
  SMART: 'smart',
  LIBRARY: 'library',
  DISCOVER: 'discover',
} as const;

export const SeekerTabSchema = GeneratedSearchTab;
export type SeekerTab = z.infer<typeof SeekerTabSchema>;

export const SeekerSortSchema = z.enum(['relevance', 'date', 'citations']);
export type SeekerSort = z.infer<typeof SeekerSortSchema>;

export const SeekerFeedbackActionSchema = z.enum([
  'accept',
  'decline',
  'save',
  'undo',
  'click',
  'impression',
]);
export type SeekerFeedbackAction = z.infer<typeof SeekerFeedbackActionSchema>;

// ═══════════════════════════════════════════════════════════════════════
// REQUEST TYPES (UI-only, not validated — frontend composes these)
// ═══════════════════════════════════════════════════════════════════════

export interface SeekerSearchFilters {
  workspace_ids?: string[];
  year_from?: number;
  year_to?: number;
  methodology_tags?: string[];
  geography_tags?: string[];
  has_tables?: boolean;
  has_figures?: boolean;
  journal?: string;
  sort?: SeekerSort;
  // Unified search filters
  entity_types?: string[];
  parent_document_id?: string;
  collection_type?: string;
  status?: string;
  category?: string;
}

export interface SeekerSearchRequest {
  query: string;
  tab: SeekerTab;
  filters?: SeekerSearchFilters;
  /** Top-level collection scope — Seeker expects this outside of filters. */
  collection_id?: string;
  page?: number;
  page_size?: number;
}

export interface SeekerFeedbackRequest {
  workspace_id: string;
  document_id: string;
  action: SeekerFeedbackAction;
  search_query?: string;
  query_id?: string;
}

// ═══════════════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS — derived from generated Zod schemas
// ═══════════════════════════════════════════════════════════════════════

/**
 * Extended SearchResult schema with RFC-028 fields (search_eligibility, has_full_text).
 * These are returned by Seeker PR3+ but not yet in the generated spec.
 * Once seeker's OpenAPI is regenerated and `pnpm generate:api` is run, the
 * .extend() can be removed — the fields will exist natively in GeneratedSearchResult.
 */
export const SeekerSearchResultSchema = GeneratedSearchResult.extend({
  search_eligibility: z.string().nullish(),
  has_full_text: z.boolean().nullish(),
});
export type SeekerSearchResult = z.infer<typeof SeekerSearchResultSchema>;

export const SeekerFacetCountsSchema = GeneratedFacetCounts;
export type SeekerFacetCounts = z.infer<typeof SeekerFacetCountsSchema>;

export const SeekerClaimResultSchema = GeneratedClaimResult;
export type SeekerClaimResult = z.infer<typeof SeekerClaimResultSchema>;

export const SeekerSearchResponseSchema = GeneratedSearchResponse;
export type SeekerSearchResponse = z.infer<typeof SeekerSearchResponseSchema>;

export const SeekerSuggestResponseSchema = GeneratedSuggestResponse;
export type SeekerSuggestResponse = z.infer<typeof SeekerSuggestResponseSchema>;

// Re-export generated request schema for reference
export const SeekerFeedbackRequestSchema = GeneratedFeedbackRequest;
